import React, { useState, useEffect } from 'react';
import {
  Card,
  Switch,
  Form,
  InputNumber,
  TimePicker,
  Button,
  Space,
  Divider,
  Row,
  Col,
  Alert,
  message,
  Popconfirm,
  Typography,
  Tag,
  Select,
  Input,
} from 'antd';
import {
  SafetyOutlined,
  WarningOutlined,
  ReloadOutlined,
  SaveOutlined,
  StopOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { api } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

interface RiskConfig {
  id: number;
  enabled: boolean;
  max_position_size: number | string;
  max_daily_trades: number;
  max_daily_loss: number | string;
  max_drawdown_percent: number | string;
  min_order_size: number | string;
  max_order_size: number | string;
  max_leverage: number;
  trading_hours_enabled: boolean;
  trading_start_time: string;
  trading_end_time: string;
  max_slippage_percent: number | string;
  price_change_limit: number | string;
  min_account_balance: number | string;
  max_position_count: number;
  blacklist_symbols: string[];
  whitelist_enabled: boolean;
  whitelist_symbols: string[];
  webhook_ip_whitelist_enabled?: boolean;
  webhook_ip_whitelist?: string[];
  emergency_stop: boolean;
}

interface RiskStatus {
  risk_enabled: boolean;
  emergency_stop: boolean;
  status: string;
}

const RiskConfig: React.FC = () => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [blacklistInput, setBlacklistInput] = useState('');
  const [whitelistInput, setWhitelistInput] = useState('');

  // 获取风控配置
  const { data: config, isLoading } = useQuery<RiskConfig>({
    queryKey: ['risk-config'],
    queryFn: () => api.riskConfig.getConfig(),
  });

  // 获取风控状态
  const { data: status } = useQuery<RiskStatus>({
    queryKey: ['risk-status'],
    queryFn: () => api.riskConfig.getStatus(),
    refetchInterval: 5000, // 每5秒刷新状态
  });

  // 更新配置
  const updateMutation = useMutation({
    mutationFn: (data: Partial<RiskConfig>) => api.riskConfig.updateConfig(data),
    onSuccess: () => {
      message.success('风控配置更新成功');
      queryClient.invalidateQueries({ queryKey: ['risk-config'] });
      queryClient.invalidateQueries({ queryKey: ['risk-status'] });
    },
    onError: (error: any) => {
      message.error(`更新失败: ${error.response?.data?.detail || error.message}`);
    },
  });

  // 快速切换
  const toggleMutation = useMutation({
    mutationFn: (data: { enabled: boolean; emergency_stop?: boolean }) =>
      api.riskConfig.toggle(data),
    onSuccess: () => {
      message.success('风控状态切换成功');
      queryClient.invalidateQueries({ queryKey: ['risk-config'] });
      queryClient.invalidateQueries({ queryKey: ['risk-status'] });
    },
    onError: (error: any) => {
      message.error(`切换失败: ${error.response?.data?.detail || error.message}`);
    },
  });

  // 重置配置
  const resetMutation = useMutation({
    mutationFn: () => api.riskConfig.reset(),
    onSuccess: () => {
      message.success('风控配置重置成功');
      queryClient.invalidateQueries({ queryKey: ['risk-config'] });
      queryClient.invalidateQueries({ queryKey: ['risk-status'] });
    },
    onError: (error: any) => {
      message.error(`重置失败: ${error.response?.data?.detail || error.message}`);
    },
  });

  // 初始化表单数据
  useEffect(() => {
    if (config) {
      const toNum = (v: any) => {
        if (v === null || v === undefined || v === '') return v;
        if (typeof v === 'number') return v;
        const n = Number(v);
        return Number.isFinite(n) ? n : v;
      };

      form.setFieldsValue({
        ...config,
        // Normalize Decimal strings like "0E-8" into plain numbers for InputNumber.
        max_position_size: toNum((config as any).max_position_size),
        max_daily_loss: toNum((config as any).max_daily_loss),
        max_drawdown_percent: toNum((config as any).max_drawdown_percent),
        min_order_size: toNum((config as any).min_order_size),
        max_order_size: toNum((config as any).max_order_size),
        max_slippage_percent: toNum((config as any).max_slippage_percent),
        price_change_limit: toNum((config as any).price_change_limit),
        min_account_balance: toNum((config as any).min_account_balance),
        webhook_ip_whitelist_enabled: Boolean((config as any).webhook_ip_whitelist_enabled),
        webhook_ip_whitelist: ((config as any).webhook_ip_whitelist || []).join('\n'),
        trading_start_time: config.trading_start_time ? dayjs(config.trading_start_time, 'HH:mm:ss') : null,
        trading_end_time: config.trading_end_time ? dayjs(config.trading_end_time, 'HH:mm:ss') : null,
      });
      setBlacklistInput(config.blacklist_symbols?.join(', ') || '');
      setWhitelistInput(config.whitelist_symbols?.join(', ') || '');
    }
  }, [config, form]);

  const handleSubmit = async (values: any) => {
    try {
      const webhookIps = String(values.webhook_ip_whitelist || '')
        .split(/[\n,]/g)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      const updateData = {
        ...values,
        trading_start_time: values.trading_start_time?.format('HH:mm:ss'),
        trading_end_time: values.trading_end_time?.format('HH:mm:ss'),
        webhook_ip_whitelist: webhookIps,
        blacklist_symbols: blacklistInput
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0),
        whitelist_symbols: whitelistInput
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0),
      };
      
      await updateMutation.mutateAsync(updateData);
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleQuickToggle = (enabled: boolean) => {
    toggleMutation.mutate({ enabled });
  };

  const handleEmergencyStop = () => {
    toggleMutation.mutate({ 
      enabled: false, 
      emergency_stop: true 
    });
  };

  const handleReset = () => {
    resetMutation.mutate();
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'enabled': return 'success';
      case 'disabled': return 'default';
      case 'emergency': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'enabled': return '风控启用';
      case 'disabled': return '风控禁用';
      case 'emergency': return '紧急停止';
      default: return '未知状态';
    }
  };

  if (isLoading) {
    return <div>加载中...</div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2}>
          <SafetyOutlined /> 风控配置
        </Title>
        <Space>
          <Tag color={getStatusColor(status?.status)} icon={<SafetyOutlined />}>
            {getStatusText(status?.status)}
          </Tag>
          {status?.emergency_stop && (
            <Tag color="error" icon={<StopOutlined />}>
              紧急停止激活
            </Tag>
          )}
        </Space>
      </div>

      {status?.emergency_stop && (
        <Alert
          message="紧急停止已激活"
          description="所有交易功能已暂停，请检查系统状态后重新启用风控。"
          type="error"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}

      <Row gutter={24}>
        {/* 快速控制面板 */}
        <Col span={24}>
          <Card title="快速控制" style={{ marginBottom: '24px' }}>
            <Space size="large">
              <div>
                <Text strong>风控开关：</Text>
                <Switch
                  checked={config?.enabled}
                  onChange={handleQuickToggle}
                  loading={toggleMutation.isPending}
                  checkedChildren="启用"
                  unCheckedChildren="禁用"
                  style={{ marginLeft: '8px' }}
                />
              </div>
              
              <Popconfirm
                title="确定要激活紧急停止吗？"
                description="这将立即停止所有交易活动"
                onConfirm={handleEmergencyStop}
                okText="确定"
                cancelText="取消"
              >
                <Button 
                  type="primary" 
                  danger 
                  icon={<StopOutlined />}
                  loading={toggleMutation.isPending}
                >
                  紧急停止
                </Button>
              </Popconfirm>

              <Popconfirm
                title="确定要重置为默认配置吗？"
                description="这将覆盖当前所有设置"
                onConfirm={handleReset}
                okText="确定"
                cancelText="取消"
              >
                <Button 
                  icon={<ReloadOutlined />}
                  loading={resetMutation.isPending}
                >
                  重置默认
                </Button>
              </Popconfirm>
            </Space>
          </Card>
        </Col>

        {/* 详细配置 */}
        <Col span={24}>
          <Card title="详细配置">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
            >
              <Row gutter={24}>
                {/* 基础风控 */}
                <Col span={12}>
                  <Card size="small" title="基础风控参数" style={{ marginBottom: '16px' }}>
                    <Form.Item
                      label="最大持仓大小"
                      name="max_position_size"
                      rules={[{ required: true, message: '请输入最大持仓大小' }]}
                    >
                      <InputNumber
                        min={0}
                        precision={2}
                        style={{ width: '100%' }}
                        addonAfter="USDT"
                      />
                    </Form.Item>

                    <Form.Item
                      label="每日最大交易次数"
                      name="max_daily_trades"
                      rules={[{ required: true, message: '请输入每日最大交易次数' }]}
                    >
                      <InputNumber
                        min={1}
                        style={{ width: '100%' }}
                        addonAfter="次"
                      />
                    </Form.Item>

                    <Form.Item
                      label="每日最大亏损"
                      name="max_daily_loss"
                      rules={[{ required: true, message: '请输入每日最大亏损' }]}
                    >
                      <InputNumber
                        min={0}
                        precision={2}
                        style={{ width: '100%' }}
                        addonAfter="USDT"
                      />
                    </Form.Item>

                    <Form.Item
                      label="最大回撤百分比"
                      name="max_drawdown_percent"
                      rules={[{ required: true, message: '请输入最大回撤百分比' }]}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        precision={2}
                        style={{ width: '100%' }}
                        addonAfter="%"
                      />
                    </Form.Item>
                  </Card>
                </Col>

                {/* 订单风控 */}
                <Col span={12}>
                  <Card size="small" title="订单风控参数" style={{ marginBottom: '16px' }}>
                    <Form.Item
                      label="最小订单大小"
                      name="min_order_size"
                      rules={[{ required: true, message: '请输入最小订单大小' }]}
                    >
                      <InputNumber
                        min={0}
                        precision={8}
                        style={{ width: '100%' }}
                        placeholder="0 表示禁用该风控"
                      />
                    </Form.Item>

                    <Form.Item
                      label="最大订单大小"
                      name="max_order_size"
                      rules={[{ required: true, message: '请输入最大订单大小' }]}
                    >
                      <InputNumber
                        min={0}
                        precision={8}
                        style={{ width: '100%' }}
                        placeholder="0 表示禁用该风控"
                      />
                    </Form.Item>

                    <Form.Item
                      label="最大杠杆"
                      name="max_leverage"
                      rules={[{ required: true, message: '请输入最大杠杆' }]}
                    >
                      <InputNumber
                        min={1}
                        max={100}
                        style={{ width: '100%' }}
                        addonAfter="倍"
                      />
                    </Form.Item>

                    <Form.Item
                      label="最大持仓数量"
                      name="max_position_count"
                      rules={[{ required: true, message: '请输入最大持仓数量' }]}
                    >
                      <InputNumber
                        min={0}
                        style={{ width: '100%' }}
                        addonAfter="个"
                        placeholder="0 表示不限制持仓数量"
                      />
                    </Form.Item>
                  </Card>
                </Col>

                {/* 时间风控 */}
                <Col span={12}>
                  <Card size="small" title="时间风控" style={{ marginBottom: '16px' }}>
                    <Form.Item
                      label="启用交易时间限制"
                      name="trading_hours_enabled"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>

                    <Form.Item
                      label="交易开始时间"
                      name="trading_start_time"
                    >
                      <TimePicker
                        format="HH:mm:ss"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>

                    <Form.Item
                      label="交易结束时间"
                      name="trading_end_time"
                    >
                      <TimePicker
                        format="HH:mm:ss"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Card>
                </Col>

                {/* Webhook 安全 */}
                <Col span={12}>
                  <Card size="small" title="Webhook 安全" style={{ marginBottom: '16px' }}>
                    <Form.Item
                      label="启用 Webhook IP 白名单"
                      name="webhook_ip_whitelist_enabled"
                      valuePropName="checked"
                    >
                      <Switch />
                    </Form.Item>

                    <Form.Item
                      label="允许的来源 IP（每行一个）"
                      name="webhook_ip_whitelist"
                      tooltip="启用后：仅允许本机/内网 IP + 这里填写的 IP 访问 /api/v1/webhook"
                    >
                      <Input.TextArea
                        rows={6}
                        placeholder={"例如:\n52.89.214.238\n34.212.75.30\n54.218.53.128\n52.32.178.7"}
                      />
                    </Form.Item>
                  </Card>
                </Col>

                {/* 价格风控 */}
                <Col span={12}>
                  <Card size="small" title="价格风控" style={{ marginBottom: '16px' }}>
                    <Form.Item
                      label="最大滑点百分比"
                      name="max_slippage_percent"
                      rules={[{ required: true, message: '请输入最大滑点百分比' }]}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        precision={2}
                        style={{ width: '100%' }}
                        addonAfter="%"
                      />
                    </Form.Item>

                    <Form.Item
                      label="价格变动限制"
                      name="price_change_limit"
                      rules={[{ required: true, message: '请输入价格变动限制' }]}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        precision={2}
                        style={{ width: '100%' }}
                        addonAfter="%"
                      />
                    </Form.Item>

                    <Form.Item
                      label="最小账户余额"
                      name="min_account_balance"
                      rules={[{ required: true, message: '请输入最小账户余额' }]}
                    >
                      <InputNumber
                        min={0}
                        precision={2}
                        style={{ width: '100%' }}
                        addonAfter="USDT"
                      />
                    </Form.Item>
                  </Card>
                </Col>

                {/* 高级设置 */}
                <Col span={24}>
                  <Card size="small" title="高级设置" style={{ marginBottom: '16px' }}>
                    <Row gutter={24}>
                      <Col span={12}>
                        <Form.Item
                          label="禁止交易的交易对"
                          extra="用逗号分隔，例如：BTCUSDT, ETHUSDT"
                        >
                          <Input.TextArea
                            value={blacklistInput}
                            onChange={(e) => setBlacklistInput(e.target.value)}
                            placeholder="输入禁止交易的交易对"
                            rows={3}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="启用白名单"
                          name="whitelist_enabled"
                          valuePropName="checked"
                        >
                          <Switch />
                        </Form.Item>
                        <Form.Item
                          label="允许交易的交易对"
                          extra="用逗号分隔，例如：BTCUSDT, ETHUSDT"
                        >
                          <Input.TextArea
                            value={whitelistInput}
                            onChange={(e) => setWhitelistInput(e.target.value)}
                            placeholder="输入允许交易的交易对"
                            rows={3}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>

              <Divider />

              <div style={{ textAlign: 'center' }}>
                <Space>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    icon={<SaveOutlined />}
                    loading={updateMutation.isPending}
                    size="large"
                  >
                    保存配置
                  </Button>
                </Space>
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default RiskConfig;
